/**
 * client/utils.ts
 * Responsabilidad: Utilidades puras del cliente (hashing, sanitizacion, storage seguro, helpers de elementos).
 *
 * Todas las funciones son puras y sin efectos secundarios, excepto las de storage
 * que acceden a localStorage con try/catch defensivo.
 */

// ============================================================================
// Hashing
// ============================================================================

/**
 * Hash no criptografico djb2 para cadenas. Determinista: mismo input, mismo output.
 * Usado para derivar la forma de las comidas del foodId de manera consistente entre clientes.
 */
export function hashString(str: string): number {
    let hash = 5381;
    for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) + hash) + str.charCodeAt(i);
        hash |= 0;
    }
    return Math.abs(hash);
}

// ============================================================================
// Sanitizacion de alias
// ============================================================================

/**
 * Sanitiza un alias en el cliente, replicando la logica del servidor.
 * Se aplica ANTES de enviar el alias a la red para reducir trabajo del servidor.
 */
export function sanitizeAlias(raw: string): string {
    if (typeof raw !== "string") return "";
    let s = raw.slice(0, 12);
    s = s.trim();
    s = s.replace(/\s+/g, " ");
    s = s.replace(/[^a-zA-Z0-9_\- ]/g, "");
    s = s.trim();
    if (s.length < 1) return "";
    return s;
}

/**
 * Genera un alias de fallback unico para la sesion.
 * Ejemplo: "Anon123", "Anon482".
 */
export function generateAnonAlias(): string {
    return `Anon${Math.floor(100 + Math.random() * 900)}`;
}

// ============================================================================
// Storage seguro
// ============================================================================

/**
 * Lee de localStorage con try/catch. Retorna null si falla o no existe.
 * Protege contra SecurityError en modo incognito estricto.
 */
export function safeGetStorage(key: string): string | null {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
}

/**
 * Escribe en localStorage con try/catch. No lanza excepcion si falla.
 */
export function safeSetStorage(key: string, value: string): void {
    try {
        localStorage.setItem(key, value);
    } catch {
        // localStorage puede estar bloqueado en modo incognito estricto
    }
}

/**
 * Limpia exhaustivamente todo el almacenamiento local asociado a la sesión de usuario,
 * protegiendo las cuentas en computadoras compartidas.
 * Preserva exclusivamente las configuraciones físicas de audio del equipo (mute y volumen).
 */
export function clearUserSessionStorage(): void {
    try {
        // 1. Recorrer en reversa para eliminar con seguridad
        for (let i = localStorage.length - 1; i >= 0; i--) {
            const key = localStorage.key(i);
            if (key) {
                // Preservar solo ajustes de hardware del navegador
                if (key === "alchem_muted" || key === "alchem_volume") {
                    continue;
                }
                if (key.startsWith("alchem_") || key === "colyseus-auth-token") {
                    localStorage.removeItem(key);
                }
            }
        }
        // 2. Limpieza de tokens remotos
        localStorage.removeItem("colyseus-auth-token");
        localStorage.removeItem("alchem_auth_token");
    } catch (err) {
        console.warn("[Storage] Error al limpiar sesión de usuario:", err);
    }
}

// ============================================================================
// Helpers de elementos
// ============================================================================

/**
 * Devuelve un nombre legible del elemento (capitalizado).
 * "fire" -> "FIRE", "water" -> "WATER"
 */
export function getElementName(element: string): string {
    return element.toUpperCase();
}
